import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { mergeInto } from 'utils/merge';
import { canUser } from 'utils/roles';

import { Sortable, SortableItem } from 'components/sortable/sortable';
import EditorContent from 'components/editor/content';
import MediaAsset from 'components/editor/movies/movieMedia/mediaAsset';

import s from 'components/editor/movies/movieMedia/mediaAssetGroup.sss';

export default class MediaAssetGroup extends EditorContent {

	get displayedValue() {
		const { dataRoot, inheritPath, path } = this.props;
		const value = jsonPointer.get(Store.get().data, `${dataRoot}${path}`);
		const inheritedValue = inheritPath && jsonPointer.get(Store.get().data, `${dataRoot}${inheritPath}`);
		return mergeInto(inheritedValue, value);
	}

	constructor(props) {
		super(props);

		this.state = {};
		this.removeDeletedInheritedData();

		this.onAssetGroupSort = this.onAssetGroupSort.bind(this);
		this.onAssetDelete = this.onAssetDelete.bind(this);
	}

	removeDeletedInheritedData() {
		const { dataRoot, inheritPath, path } = this.props;
		const inheritedAssets = jsonPointer.get(Store.get().data, `${dataRoot}${inheritPath}`) || {};
		Object.entries(inheritedAssets)
			.filter(([, asset]) => asset === null)
			.forEach(([assetKey,]) => {
				jsonPointer.removeDeep(Store.get().data, `${dataRoot}${path}/${assetKey}`);
			});
	}

	getSortedAssets() {
		// Convert to array with original keys preserved and sort by order field
		return Object.entries(this.displayedValue || {})
			.map(([key, asset]) => ({ key, asset }))
			.sort((a, b) => (a.asset.order ?? 99) - (b.asset.order ?? 99));
	}

	getNewAssetId(assets = this.getSortedAssets()) {
		const currentId = this.state.newAssetId;

		// If there is no currentId or if the currentId already exists in assets, generate a new one
		if (!currentId || assets.some(({ key }) => key === currentId)) {
			if (currentId) this.updateAssetOrder(); // Update the order of existing assets before generating a new ID

			// Generate a new unique ID
			const newId = crypto.randomUUID();
			this.setState({ newAssetId: newId });
			return newId;
		}

		return currentId;
	}

	updateAssetOrder(assets = this.getSortedAssets().map(({ key }) => key)) {
		const { dataRoot, path } = this.props;
		const assetsDict = this.displayedValue || {};

		// Update the order of each asset based on its new position in the list, only when necessary
		assets.forEach((id, index) => {
			if (assetsDict[id]?.order !== index) {
				jsonPointer.setDeep(Store.get().data, `${dataRoot}${path}/${id}/order`, index);
			}
		});
	}

	onAssetGroupSort(e) {
		const allItems = [...e.target.querySelectorAll('[data-asset]')].map((el) => el.dataset.asset);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);

		this.updateAssetOrder(allItems);
	}

	onAssetDelete() {
		this.updateAssetOrder();
	}

	renderVisual() {
		const readonly = !canUser('write');
		const { assetLabel, hideAdd, inheritPath, path, sortable } = this.props;

		const assetsWithKeys = this.getSortedAssets();
		const newAssetId = this.getNewAssetId(assetsWithKeys);
		
		return (
			<Sortable horizontal slop={8} class={s.assetsRow} onChange={this.onAssetGroupSort} disabled={readonly || !sortable}>
				{!hideAdd && <MediaAsset {...this.props} label="" path={`${path}/${newAssetId}`} inheritPath={null} />}
				{assetsWithKeys.map(({ key }) => (
					<SortableItem grabbable class={s.itemWrapper} key={key} data-asset={key}>
						<MediaAsset {...this.props} label={assetLabel || ''} path={`${path}/${key}`} inheritPath={`${inheritPath}/${key}`} />
					</SortableItem>
				))}
			</Sortable>
		);
	}

	renderSettings() {
		const { activeElement, assetLabel, hideDelete, inheritPath, label, path, sortable } = this.props;
		if (!activeElement.startsWith(path)) return null; // Only render settings if the active element is within this group

		const imageId = activeElement.split('/').pop();
		const data = this.displayedValue?.[imageId];
		const assetNum = data?.order !== undefined ? data.order + 1 : null;
		const settingsLabel = assetLabel
			? (typeof assetLabel === 'function' ? assetLabel(data) : assetLabel)
			: `${label} ${assetNum || ''}`.trim();
		const canRemove = sortable && !hideDelete;

		return (
			<MediaAsset
				{...this.props}
				canRemove={canRemove}
				label={settingsLabel}
				path={activeElement}
				inheritPath={`${inheritPath}/${imageId}`}
				onDelete={this.onAssetDelete}
			/>
		);
	}
}